/** @module Specification/SpecialMaterial */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi/_base/underscore",
	"csi/config",
	"csi/def",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/form/Widget",
	"dojo/store/Memory",
	"csi/xml",
	"csi/xml/Query",
	"Apparel/ProductType",
	"./BOM/BOMSectionMixin",
	"./BOM/PartMaterialRequireSectionMixin"
], function(declare, lang, _, config, def, Field, Relationship, Widget, Memory, xml,
			Query, ProductType, BOMSectionMixin, PartMaterialRequireSectionMixin) {

	return declare([Widget, BOMSectionMixin, PartMaterialRequireSectionMixin], {

		PRODUCT_TYPE: "ProductType",
		FIELD_CREATE_ASSEMBLY: "CreateAssembly",
		FIELD_BOM_SUBTYPE: "MaterialBOMSubtype",
		FIELD_BOM_SECTION: "DSSection",
		FIELD_BOM_SECTION_LABEL: "BomSection",

		bomSectionQuery: null,
		/** @property {Boolean} isCreateAssemblyAvailable - shows if the Create Assembly Checkbox is down or not */
		isCreateAssemblyAvailable: false,
		/** @property {Boolean} exposeBOMSupplierAttributes - checks the subtype boolean if BOMSupplierAttributes are enabled */
		exposeBOMSupplierAttributes: false,

		/** @property {Boolean} isRequireSection - checks if RequireSectionForPlacement is enabled and if no section is selected */
		isRequireSection: false,

		/**
		 * Updates the visibility of the Subtype Field based on the value of the CreateAssembly Checkbox
		 * @param {Field} field
		 */
		_updateFieldVisibility: function(field) {
			const createAssemblyValue = this.defView.getFieldValue(this.FIELD_CREATE_ASSEMBLY);
			this.setFieldHidden(this.FIELD_BOM_SUBTYPE, !createAssemblyValue);
		},

		/**
		 * Grabs the value of the CreateAssembly Checkbox
		 * Based on the value return a list of ProductTypes
		 * return {Query}
		 */
		getProductTypes: function() {
			let bAssembly = false;
			if (this.isCreateAssemblyAvailable) {
				const field = this.getField(this.FIELD_CREATE_ASSEMBLY);
				// get runtime value from the actual form if controller exists or initial value from local storage
				bAssembly = field.m_control ? field.getValue() : this.getValueForField(field);
			}

			let q;
			if (bAssembly) {
				q = new Query()
					.Attr("Available", xml.OP_EQUALS, true, "Child:ProductionConfig")
					.Attr2("TDSMap", xml.OP_EQUALS, {
						Index: "MaterialBOM",
						Path: "Child:ProductionConfig",
						SValue: true
					})
					.Attr2("MaterialUsage", xml.OP_EQUALS, {
						Path: "Child:ProductionConfig",
						SValue: "MaterialUsage:Standalone"
					});
			} else {
				q = new Query()
					.Attr("Available", xml.OP_EQUALS, true, "Child:ProductionConfig")
					.Attr2("MaterialUsage", xml.OP_EQUALS, {
						Path: "Child:ProductionConfig",
						SValue: "MaterialUsage:Standalone"
					});
			}

			if (this.bomSectionQuery) {
				q.importQuery(this.bomSectionQuery);
			}

			if (this.getField(this.PRODUCT_TYPE)) {
				this._applyRequireSectionFiltersFromSpecialPartMaterial(q);
			}

			return q;
		},

		/**
		 * Updates the values of the Product_Type Dropdown
		 * @param {Field} field
		 */
		getFieldStore: function(field) {
			if (field.m_id === this.PRODUCT_TYPE) {
				field.m_relationship.m_query = this.getProductTypes();
			}

			if (field.getControlName() === this.FIELD_BOM_SECTION_LABEL) {
				return this.filterBomSections(this.getFieldValue(this.PRODUCT_TYPE));
			}

			return this.inherited(arguments);
		},

		/**
		 * Extension point called when assembly checkbox field changed
		 */
		onChangeAssembly: function() {
		},

		/**
		 * When the checkbox is updated this function updates the productTypes saved value of the Checkbox
		 */
		_onChangeCreateAssembly: function() {
			this.onChangeAssembly();

			let field = this.getField(this.PRODUCT_TYPE);

			// code from csi/form/Widget#_onDependency
			field.m_control.set("store", this.getFieldStore(field));
		},


		/**
		 * Add CreateAssembly Checkbox and MaterialSubtype Fields on Resolving of the Form view
		 * Additionally the ProductTypes are updated to either show all Material Types or only Material Types with BOMs
		 */
		_resolveView: function() {
			// add fields from the form of parent node
			let fields = this.defView.m_fields;
			if (this.isCreateAssemblyAvailable) {
				fields.push(lang.mixin(new Field(), {
					m_label: this.FIELD_CREATE_ASSEMBLY,
					m_typeControl: def.FIELD_CHECKBOX,
					m_onChange: "_onChangeCreateAssembly",
					bPersist: true
				}));

				fields.push(lang.mixin(new Field(), {
					m_label: this.FIELD_BOM_SUBTYPE,
					idParameter: this.FIELD_BOM_SUBTYPE,
					m_bRequired: true,
					m_typeControl: def.FIELD_FILTERING_SELECT,
					m_relationship: lang.mixin(new Relationship(), {
						m_typeNode: "MaterialBOMSubtype",
						m_query: new Query()
							.Attr("Active", xml.OP_EQUALS, true, "Child:Settings")
							.Attr("ExposeBOMSupplierAttributes", xml.OP_EQUALS, this.exposeBOMSupplierAttributes, "Child:Settings")
					})
				}));
			}

			const query = this.getProductTypes();

			fields.unshift(lang.mixin(new Field(), {
				m_id: this.PRODUCT_TYPE,
				m_onChange: "onChangeProductTypeField",
				m_relationship: lang.mixin(new Relationship(), {
					m_typeNode: "MaterialType",
					m_query: query.getElements()
				})
			}));

			const hasSelectedSection = this.getFormData().m_source._groupResults?.length > 0;
			this.isRequireSection = this._isRequireSectionForPlacement(this.defView.m_formData?.getParent()) && !hasSelectedSection;
			if (this.isRequireSection) {
				this.defView.addFieldAfter(lang.mixin(new Field(), {
					m_label: this.FIELD_BOM_SECTION_LABEL,
					m_dependency: this.PRODUCT_TYPE,
					idParameter: this.FIELD_BOM_SECTION,
					m_bRequired: true,
					m_typeControl: def.FIELD_FILTERING_SELECT
				}), this.PRODUCT_TYPE);
			}

			this.inherited(arguments);
		},

		filterBomSections: function(materialType) {
			const revision = this.defView.m_formData?.getParent();
			const revisionSections = revision?.AllSections;
			const sessionsData = [];

			revisionSections?.forEach((sectionURL) => {
				const sectionData = this.cacheView.get(sectionURL);
				const placementProductTypes = sectionData?.PlacementProductTypes;
				if (placementProductTypes?.length === 0 || placementProductTypes?.includes(materialType)) {
					sessionsData.push({id: sectionData.$URL, name: sectionData.$Name});
				}
			});

			return new Memory({data: sessionsData});
		},

		/**
		 * enable/disable the createAssembly Field based on the material type
		 * @param {View} defView
		 * @param {Field} field
		 * @param {Any} value
		 */
		onChangeProductTypeField: function(defView, field, value) {
			const hasBOM = ProductType.getTypeTDSSetting(value, "MaterialBOM");
			this.setFieldDisabled(this.getField(this.FIELD_CREATE_ASSEMBLY), !hasBOM);
		},

		/**
		 * @override
		 * Need to adjust this piece when we implement aggregate store for material type
		 */
		onWriteFormReport(field, writer, formInfo) {
			const {nodeUrl} = formInfo;
			const parentUrl = this.defView.m_formData?.getParent().$URL;

			// reset so we don't attach Material directly to ApparelBOMRevision
			formInfo.parentUrl = null;

			// create PartMaterial and attach, wrap the material with this PartMaterial node
			writer
				.createNode(null, "PartMaterial")
				.attach(parentUrl, "Items")
				.changeAttribute("Actual", xml.ATTR_REF, nodeUrl);
		},

		/**
		 * @override
		 * Material types should be filtered on ApplicablePlacementProductTypes of
		 * the BOMSectionDefinition if we are grouped
		 */
		onViewCreate: function() {
			this.inherited(arguments);
			const fd = this.getFormData();
			const self = this;
			let bomSectionQuery = new Query();
			if (fd.m_source._groupResults) {
				_.each(fd.m_source._groupResults, function(gr) {
					self.applyBomSectionQuery(gr.groupValue, bomSectionQuery, self.cacheView, true);
				});
			}
			this.bomSectionQuery = bomSectionQuery;
		}
	});
});